import { FillsGraphTimeSeriesData } from 'communication/types/fillsGraph/FillsGraphTimeSeriesData';
import { NumberFormat } from '@instinet-analytics/core-lib';
import {
    getActualValue,
    getInterpolatedValue,
    InterpolationContext,
    isValueWithInterpolation,
    toValueWithInterpolation,
    ValueWithInterpolation,
} from 'utils/interpolation';
import { TimeSeriesChartDataItem } from 'utils/components/TimeSeriesChart/TimeSeriesChartData';
import {
    mapFromElementRangeArray,
    TimeSeriesDisableRange,
    TimeSeriesDisableRangeLabel,
} from 'utils/timeseries/TimeSeriesDisableRange';
import {
    getItemTimeOrLastItem,
    sortTimeSeries,
} from 'utils/timeseries/timeSeriesUtils';
import { useFillsGraphConfigurationSelector } from './store/configuration/selectors';
import { useSelectFillsGraphStreamState } from './store/data/selectors';
import { FillsGraphConfiguration } from './store/configuration/FillsGraphConfiguration';
import {
    bucket,
    BucketedData,
    ensureEndOfLastBucketWithActualVolumeOrFillVolumeIsInTimeDomain,
    mergeBucketedData,
} from './fillsGraphDataBuckets';
import {
    computeStrategyParamsTimeseries,
    mergeStrategyParamSeries,
    TimeSeriesStrategyParams,
} from './fillsGraphStrategyParamsData';
import { getSideClass } from 'utils/css/cssUtils';
import { FillsGraphDataState } from 'widgets/fillsGraph/store/data/FillsGraphDataState';
import { FillsGraphPortfolioData } from 'widgets/fillsGraph/fillsGraphPortfolioData';
import { OrderSide } from 'communication/types/order/OrderSide';
import { FillsGraphOrderStatData } from 'communication/types/fillsGraph/FillsGraphOrderStatData';
import {
    useSelectedSymbolExternalMarketData,
    useSymbolExternalMarketData,
} from 'marketData/hooks';
import {
    MarketDataBarItem,
    MarketDataRefData,
} from 'marketData/store/MarketDataState';
import {
    enrichBucketsWithExternalMarketData,
    enrichTimeSeriesWithExternalMarketData,
    volumesArePercentages,
} from 'widgets/fillsGraph/marketData';
import { StreamMessage } from 'utils/messages/StreamMessage';
import { useSymbol } from 'symbols/symbolInfo/hooks';
import { SymbologyValues } from 'app/store/appSettings/AppSettingsState';
import { useSelectAppSettings } from 'app/store/appSettings/selectors';
import { calculateTimeDomain } from 'utils/components/TimeSeriesChart/timeSeriesTimeScale';
import { fillsExternalMarketDataBarSize } from 'widgets/fillsGraph/store/data/marketData';
import { FillsGraphPlChartItem } from 'widgets/fillsGraph/pl/fillsGraphPlData';

export interface OrderFillInterpolatedValues {
    price?: ValueWithInterpolation;
    vwap?: ValueWithInterpolation;
    percentDealt?: ValueWithInterpolation;
    avgExec?: ValueWithInterpolation;
    totalPnl?: ValueWithInterpolation;
    realizedPnl?: ValueWithInterpolation;
    unrealizedPnl?: ValueWithInterpolation;
    relativePerfIndex?: ValueWithInterpolation;
    relativePerfOrderToIndex?: ValueWithInterpolation;
    normalisedBenchmarkPrice?: ValueWithInterpolation;
    normalisedBenchmarkPriceBps?: ValueWithInterpolation;
}

export interface OrderFill
    extends OrderFillInterpolatedValues,
        TimeSeriesChartDataItem,
        Omit<TimeSeriesStrategyParams, 'revisionIndex'> {
    fillPrice?: number;
    snapPrice?: number;
    bucket?: BucketedData;
    percCompleted?: number;
}

export interface FillsGraphData {
    arrivalTimestamp: number;
    endTimestamp?: number;
    timeCompleted: number;
    bucketedData: BucketedData[];
    data: OrderFill[];
    name?: string;
    impactCost?: number;
    sideClass: string;
    snapPrice?: number;
    closePrice?: number;
    open?: number;
    breaksInTrading: TimeSeriesDisableRange[];
    rangeLabels: TimeSeriesDisableRangeLabel[];
    secondarySymbol?: string;
    strategyParameterData?: TimeSeriesStrategyParams[];
    relativePerformanceIndex: string;
    strategyParametersLimitPriceAvailable: boolean;
    tickSize: number | null | undefined;
    volumesArePercentages?: boolean;
    externalMarketDataMessages: StreamMessage[];
}

export const deriveAverageExecutionPrice = (d: OrderFill) =>
    getInterpolatedValue(d.avgExec);
export const derivePrice = (d: OrderFill) => getInterpolatedValue(d.price);
export const deriveLimitPrice = (d: OrderFill) => d.limitPrice;
export const deriveMinParticipationRate = (d: OrderFill) =>
    d.minParticipationRate;
export const deriveMaxParticipationRate = (d: OrderFill) =>
    d.maxParticipationRate;
export const deriveVwap = (d: OrderFill) => getInterpolatedValue(d.vwap);
export const deriveRelativePerfIndex = (d: OrderFill) =>
    getInterpolatedValue(d.relativePerfIndex);
export const deriveRelativePerfOrderToIndex = (d: OrderFill) =>
    getInterpolatedValue(d.relativePerfOrderToIndex);
export const deriveNormalisedBenchmarkPrice = (d: OrderFill) =>
    getInterpolatedValue(d.normalisedBenchmarkPrice);

export const deriveCandleOpen = (d: OrderFill) =>
    d.bucket?.candle && d.bucket?.candle.open;
export const deriveCandleHigh = (d: OrderFill) =>
    d.bucket?.candle && d.bucket?.candle.high;
export const deriveCandleLow = (d: OrderFill) =>
    d.bucket?.candle && d.bucket?.candle.low;
export const deriveCandleClose = (d: OrderFill) =>
    d.bucket?.candle && d.bucket?.candle.close;

export const deriveOrderFillBucketMidTime = (d: OrderFill) =>
    d.bucket && d.bucket.midTime;
export const deriveOrderFillBucketFillPrice = (d: OrderFill) =>
    d.bucket && d.bucket.price;
export const deriveOrderFillBucketFillVolume = (d: OrderFill) =>
    d.bucket && d.bucket.fillVolume;

export const deriveBucketMidTime = (d: BucketedData) => d.midTime;
export const deriveBucketActualVolume = (d: BucketedData) => d.actualVolume;
export const deriveBucketExpectedVolume = (d: BucketedData) => d.expectedVolume;
export const deriveBucketPercentOfMarketVolume = (d: BucketedData) =>
    d.percentOfMarketVolume;
export const deriveBucketAvgPercentOfMarketVolume = (d: BucketedData) =>
    d.averagePercentOfMarketVolume || 0;

const MS_IN_MINUTE = 60 * 1000;

export const initialData: FillsGraphData = {
    arrivalTimestamp: 0,
    timeCompleted: 0,
    bucketedData: [],
    data: [],
    name: undefined,
    impactCost: undefined,
    sideClass: '',
    breaksInTrading: [],
    rangeLabels: [],
    relativePerformanceIndex: '',
    strategyParametersLimitPriceAvailable: false,
    tickSize: undefined,
    volumesArePercentages: false,
    externalMarketDataMessages: [],
};

export function useFillsGraphData(
    portfolio: FillsGraphPortfolioData | undefined
): FillsGraphData {
    const fillsGraphStreamState = useSelectFillsGraphStreamState();

    const {
        barData: dapiPriceData,
        refData: dapiRefData,
        messages: dapiMessages,
    } = useSelectedSymbolExternalMarketData(fillsExternalMarketDataBarSize);

    const config = useFillsGraphConfigurationSelector();
    const { symbology } = useSelectAppSettings();

    const {
        general: { fallbackIndex },
    } = config;
    const {
        barData: dapiIndexPriceData,
        refData: dapiIndexRefData,
        messages: indexDapiMessages,
    } = useSymbolExternalMarketData(
        fallbackIndex,
        symbology,
        fillsExternalMarketDataBarSize
    );

    const externalMarketDataMessages = [...dapiMessages, ...indexDapiMessages];

    if (!portfolio || !fillsGraphStreamState || !fillsGraphStreamState.data) {
        return initialData;
    }

    return getFillsGraphData(
        fillsGraphStreamState.data,
        config,
        portfolio.side,
        dapiPriceData,
        dapiRefData,
        dapiIndexPriceData,
        dapiIndexRefData,
        externalMarketDataMessages
    );
}

export function getFillsGraphData(
    fillsGraphData: FillsGraphDataState,
    config: FillsGraphConfiguration,
    side: OrderSide,
    dapiPriceData: MarketDataBarItem[] | undefined,
    dapiRefData: MarketDataRefData | undefined,
    dapiIndexPriceData: MarketDataBarItem[] | undefined,
    dapiIndexRefData: MarketDataRefData | undefined,
    externalMarketDataMessages: StreamMessage[]
): FillsGraphData {
    const {
        general: {
            barSizeInMinutes: bucketSizeInMinutes,
            useSecondarySymbol,
            fallbackIndex,
        },
    } = config;

    const orderStatData = extractOrderStatData(fillsGraphData?.orderStat);
    const relativePerformanceIndex =
        orderStatData?.secondarySymbol && useSecondarySymbol
            ? orderStatData?.secondarySymbol
            : fallbackIndex;

    return {
        ...initialData,
        volumesArePercentages: volumesArePercentages(
            dapiPriceData,
            dapiRefData
        ),
        open: fillsGraphData.orderOpen?.v,
        sideClass: getSideClass(side),
        ...extractTimeSeriesData(
            fillsGraphData,
            orderStatData,
            side,
            bucketSizeInMinutes,
            dapiPriceData,
            dapiRefData,
            dapiIndexPriceData,
            dapiIndexRefData
        ),
        ...orderStatData,
        relativePerformanceIndex,
        externalMarketDataMessages,
    };
}

export interface FillsGraphDataOrderStat {
    closePrice: number;
    name: string;
    secondarySymbol: string;
    tickSize: number | null | undefined;
    arrivalTimestamp: number;
    endTimestamp?: number;
}

export function extractOrderStatData(
    orderStat?: FillsGraphOrderStatData
): FillsGraphDataOrderStat | undefined {
    if (!orderStat) {
        return undefined;
    }

    return {
        closePrice: orderStat!.p,
        name: orderStat!.n,
        secondarySymbol: orderStat!.ss,
        tickSize: orderStat!.tickSize,
        arrivalTimestamp: orderStat!.a,
        endTimestamp: orderStat!.e,
    };
}

function extractTimeSeriesData(
    fillsGraphData: FillsGraphDataState,
    orderStatData: FillsGraphDataOrderStat | undefined,
    side: OrderSide,
    bucketSizeInMinutes: number,
    dapiPriceData: MarketDataBarItem[] | undefined,
    dapiRefData: MarketDataRefData | undefined,
    dapiIndexPriceData: MarketDataBarItem[] | undefined,
    dapiIndexRefData: MarketDataRefData | undefined
) {
    const { timeSeries, costUpdate, snap, disableRange, strategyParams } =
        fillsGraphData;

    if (!timeSeries || timeSeries.length === 0) {
        return undefined;
    }

    const sortedTimeSeries = sortTimeSeries(timeSeries, 1);

    const impactCost = costUpdate ? costUpdate.qty : undefined;

    let snapMinute;
    let snapPrice;
    if (snap) {
        ({ p: snapPrice, _i: snapMinute } = snap);
    }

    const breaksInTrading = mapFromElementRangeArray(disableRange);

    const rangeLabels = extractRangeLabels(sortedTimeSeries, breaksInTrading);

    const interpolationContext = new InterpolationContext<OrderFill>(
        'backward'
    );
    const interpolationContextVwap = new InterpolationContext<OrderFill>(
        'backward'
    );

    const data: OrderFill[] = [];
    const inDomain = sortedTimeSeries.filter((d) => !!d.c || !!d.st || !!d.bt);
    const timeCompleted: number =
        (inDomain.length > 0
            ? inDomain[inDomain.length - 1]._i
            : sortedTimeSeries[sortedTimeSeries.length - 1]._i) * MS_IN_MINUTE;

    let d: FillsGraphTimeSeriesData;

    const targetInterpolationIndex = getTargetInterpolationIndex(
        sortedTimeSeries,
        timeCompleted
    );

    const endDateOrEndOfDomain = orderStatData?.endTimestamp ?? timeCompleted;
    const targetInterpolationIndexVwap = getTargetInterpolationIndex(
        sortedTimeSeries,
        endDateOrEndOfDomain,
        false
    );

    for (let i = sortedTimeSeries.length - 1; i >= 0; i--) {
        d = sortedTimeSeries[i];

        const time = d._i * MS_IN_MINUTE;

        const afterEndDateOrEndOfDomain =
            endDateOrEndOfDomain && time > endDateOrEndOfDomain;

        data[i] = {
            time,
            price: toValueWithInterpolation(d.c),

            fillPrice: d.s,
            percentDealt: toValueWithInterpolation(d.pc),
            avgExec: toValueWithInterpolation(side === 'B' ? d.bAvg : d.sAvg),
            totalPnl: toValueWithInterpolation(d.tPL),
            realizedPnl: toValueWithInterpolation(d.rPL),
            relativePerfIndex: toValueWithInterpolation(d.ich),
            relativePerfOrderToIndex: toValueWithInterpolation(d.rch),
            normalisedBenchmarkPrice: toValueWithInterpolation(d.index_c),
            normalisedBenchmarkPriceBps: toValueWithInterpolation(d.index_bps),
        };

        interpolationContext.interpolateStep(
            data,
            [
                'percentDealt',
                'avgExec',
                'price',
                'normalisedBenchmarkPrice',
                'normalisedBenchmarkPriceBps',
                'totalPnl',
                'realizedPnl',
                'relativePerfOrderToIndex',
                'relativePerfIndex',
            ],
            i,
            targetInterpolationIndex
        );

        if (!afterEndDateOrEndOfDomain) {
            data[i].vwap = toValueWithInterpolation(d.w);
            data[i].unrealizedPnl = toValueWithInterpolation(d.urPL);

            interpolationContextVwap.interpolateStep(
                data,
                ['vwap', 'unrealizedPnl'],
                i,
                targetInterpolationIndexVwap
            );
        }

        if (snapMinute && snapMinute <= d._i) {
            data[i].snapPrice = snapPrice;
        }
    }

    let strategyParameterData;
    let strategyParametersLimitPriceAvailable = false;
    if (strategyParams && data.length > 0) {
        strategyParameterData = computeStrategyParamsTimeseries(
            strategyParams,
            data[0].time
        );

        mergeStrategyParamSeries(data, strategyParameterData);

        if (strategyParameterData.find((s) => s.limitPrice !== undefined)) {
            strategyParametersLimitPriceAvailable = true;
        }
    }

    const bucketedData = bucket(
        bucketSizeInMinutes,
        sortedTimeSeries,
        breaksInTrading,
        orderStatData?.endTimestamp
    );

    mergeBucketedData(data, bucketedData);

    for (const d of data) {
        d.percCompleted = d.percentDealt == null ? 0 : d.percentDealt.value;
    }

    const newEndTime =
        ensureEndOfLastBucketWithActualVolumeOrFillVolumeIsInTimeDomain(
            data,
            bucketedData
        );

    if (dapiPriceData && dapiPriceData.length > 0) {
        enrichTimeSeriesWithExternalMarketData(
            data,
            dapiPriceData,
            dapiRefData,
            dapiIndexPriceData,
            dapiIndexRefData,
            orderStatData
        );
        enrichBucketsWithExternalMarketData(
            bucketedData,
            dapiPriceData,
            dapiRefData
        );
    }

    return {
        timeCompleted: Math.max(timeCompleted, newEndTime ?? 0),
        bucketedData,
        data,
        impactCost,
        snapPrice,
        breaksInTrading,
        rangeLabels: rangeLabels,
        strategyParameterData,
        strategyParametersLimitPriceAvailable,
    };
}

export interface FillsGraphLegendData {
    name?: string;
    boughtOrSold: 'Bought' | 'Sold';
    totalOrderQty: string;
    totalFillQty: string;
    avgFillPrice: string;
    percentageFilled: string;
}

export function formatToolbarLegendData(
    fillsGraphData: FillsGraphData,
    fillsGraphPortfolioData: FillsGraphPortfolioData,
    numberFormat: NumberFormat,
    avgExecAndVwapDecimals: number
): FillsGraphLegendData {
    const { name } = fillsGraphData;
    const {
        side,
        totalOrderQty,
        totalFillQty,
        avgFillPrice,
        percentageFilled,
    } = fillsGraphPortfolioData;

    return {
        name: name,
        boughtOrSold: side === 'B' ? 'Bought' : 'Sold',
        totalOrderQty: numberFormat.n0(totalOrderQty),
        totalFillQty: numberFormat.n0(totalFillQty),
        avgFillPrice: numberFormat.n(avgExecAndVwapDecimals, avgFillPrice),
        percentageFilled: numberFormat.n0(percentageFilled) + '%',
    };
}

export function getBucketWithFillAtTime(
    fillsGraphData: FillsGraphData,
    time: number | undefined,
    fillHoverInterval: number
): BucketedData | undefined {
    const { data } = fillsGraphData;

    const beforeTime =
        time &&
        data
            .filter((d) => {
                const bd = d.bucket;
                if (!bd) {
                    return false;
                }

                // Find the hover time range for this bucket based on the fillHoverWidth
                const midTime = bd.midTime;
                const halfInterval = Math.round(fillHoverInterval / 2);
                const hoverStart = midTime - halfInterval;
                const hoverEnd = midTime + halfInterval;

                // Match this bucket if it has fill data and time is within the hover range...
                return (
                    (bd.price || bd.fillVolume) &&
                    time >= hoverStart &&
                    time <= hoverEnd
                );
            })
            .map((d) => d.bucket!);
    return beforeTime ? beforeTime[beforeTime.length - 1] : undefined;
}

export function getBucketAtTimeOrEnd(
    fillsGraphData: FillsGraphData,
    time: number | undefined
): BucketedData | undefined {
    const { data, timeCompleted } = fillsGraphData;

    time = time || timeCompleted;

    const beforeTime =
        time &&
        data
            .filter((d) => !!d.bucket)
            .map((d) => d.bucket!)
            .filter((bd) => {
                // Match this bucket if time is within the bucket...
                return time! >= bd.startTime && time! <= bd.endTime;
            });

    return beforeTime ? beforeTime[beforeTime.length - 1] : undefined;
}

export function getPnlLegendData(
    plData: FillsGraphPlChartItem[],
    numberFormat: NumberFormat,
    time: number | undefined
) {
    const d = getItemTimeOrLastItem(plData, time);
    if (d === undefined) {
        return {};
    }

    const realizedPnl =
        d.realizedPnl === undefined && d['pos-realizedPnl'] === 0
            ? 0
            : d.realizedPnl;

    return {
        realized: numberFormat.n2(realizedPnl),
        unrealized: numberFormat.n2(d.unrealizedPnl),
        total: numberFormat.n2(d.totalPnl),
    };
}

export function formatRelPerfIndexLegendData(
    fillsGraphData: FillsGraphData,
    numberFormat: NumberFormat,
    time: number | undefined
) {
    const interpolatedValues: OrderFillInterpolatedValues = {
        relativePerfIndex: undefined,
        relativePerfOrderToIndex: undefined,
    };

    if (time !== undefined) {
        const d = getOrderFillAtTimeOrEnd(fillsGraphData, time);

        if (d === undefined) {
            return {
                index: undefined,
                perVsIndex: undefined,
            };
        }
        interpolatedValues.relativePerfIndex = d.relativePerfIndex;
        interpolatedValues.relativePerfOrderToIndex =
            d.relativePerfOrderToIndex;
    } else {
        fillLatestNonInterpolatedValues(fillsGraphData, interpolatedValues);
    }

    return {
        relativePerfIndex: numberFormat.n4(
            getActualValue(interpolatedValues.relativePerfIndex)
        ),
        relativePerfOrderToIndex: numberFormat.n4(
            getActualValue(interpolatedValues.relativePerfOrderToIndex)
        ),
    };
}

export function getOrderFillAtTimeOrEnd(
    fillsGraphData: FillsGraphData,
    time: number | undefined
): OrderFill | undefined {
    const { data, timeCompleted } = fillsGraphData;

    time = time || timeCompleted;

    const beforeTime = time && data.filter((d) => d.time <= time!);

    return beforeTime ? beforeTime[beforeTime.length - 1] : undefined;
}

export function getNonInterpolatedAtTimeOrEnd(
    fillsGraphData: FillsGraphData,
    time: number | undefined,
    property: keyof OrderFill
): OrderFill | undefined {
    const { data } = fillsGraphData;

    const filterToNotInterpolated = (o: OrderFill) => {
        const value = o[property];

        return isValueWithInterpolation(value) && !value.isInterpolated;
    };
    const nonInterpolated = data.filter(filterToNotInterpolated);
    const items = time
        ? nonInterpolated.filter((d) => d.time <= time)
        : nonInterpolated;

    if (items.length === 0) {
        return undefined;
    }

    return items[items.length - 1];
}

/*
 * Fills in the values for the keys of the passed in Partial OrderFill with the latest non-interpolated values
 */
export function fillLatestNonInterpolatedValues(
    fillsGraphData: FillsGraphData,
    objectToFill: OrderFillInterpolatedValues
) {
    const keys = Object.keys(objectToFill);
    let populatedCount = 0;
    for (let i = fillsGraphData.data.length - 1; i >= 0; i--) {
        const item = fillsGraphData.data[i];
        for (const k of keys) {
            const key = k as keyof OrderFillInterpolatedValues;
            const foundValue = objectToFill[key];
            if (foundValue === undefined) {
                const itemValue = item[key];
                if (
                    isValueWithInterpolation(itemValue) &&
                    !itemValue.isInterpolated
                ) {
                    objectToFill[key] = itemValue;
                    populatedCount++;
                    if (populatedCount === keys.length) {
                        return;
                    }
                }
            }
        }
    }
}

export interface SignedDataSectionItem {
    time: number;
    value: number;
}

function extractRangeLabels(
    sortedTimeSeries: FillsGraphTimeSeriesData[],
    breaksInTrading: TimeSeriesDisableRange[]
): TimeSeriesDisableRangeLabel[] {
    if (sortedTimeSeries.length === 0) {
        return [];
    }

    const ret: TimeSeriesDisableRangeLabel[] = [];

    if (sortedTimeSeries[0].si) {
        ret.push({
            open: sortedTimeSeries[0]._i * 60000,
            openLabel: sortedTimeSeries[0].si,
        });
    }

    for (const br of breaksInTrading) {
        const oneMinBeforeStart = sortedTimeSeries.find(
            (s) => s._i * 60000 + 60000 === br.from
        );
        const end = sortedTimeSeries.find((s) => s._i * 60000 === br.to);

        ret.push({
            close: (oneMinBeforeStart ? oneMinBeforeStart._i : br.from) * 60000,
            open: br.to,
            closeLabel: oneMinBeforeStart ? oneMinBeforeStart.si : undefined,
            openLabel: end ? end.si : '',
        });
    }

    return ret;
}

export function getTargetInterpolationIndex(
    sortedTimeSeries: FillsGraphTimeSeriesData[],
    timeCompleted: number,
    useNowIfInRange = true
): number {
    const timeCompletedItem = getItemForTime(sortedTimeSeries, timeCompleted);
    const timeCompletedIndex =
        timeCompletedItem !== undefined
            ? sortedTimeSeries.indexOf(timeCompletedItem)
            : sortedTimeSeries.length - 1;

    const nowItem = useNowIfInRange
        ? getItemForTime(sortedTimeSeries, new Date().getTime())
        : undefined;

    // Use now if in the range, otherwise time completed...
    return nowItem ? sortedTimeSeries.indexOf(nowItem) : timeCompletedIndex;
}

function getItemForTime(data: FillsGraphTimeSeriesData[], time: number) {
    if (time > data[data.length - 1]._i * MS_IN_MINUTE) {
        // After end of time series
        return undefined;
    }

    const beforeItems = data.filter((x) => x._i * MS_IN_MINUTE <= time);
    return beforeItems.length > 0
        ? beforeItems[beforeItems.length - 1]
        : undefined;
}

export function useIndexSymbolUsersSymbology(data?: FillsGraphDataState) {
    const { symbology } = useSelectAppSettings();
    return useSymbol(
        data?.orderStat?.indexSym,
        SymbologyValues.Instinet,
        symbology
    );
}

export function calculateFillsGraphTimeDomain(
    fillsGraphData: FillsGraphData
): [number, number] {
    const { data } = fillsGraphData;

    const timeDomain = calculateTimeDomain(data);
    const startTime = timeDomain[0];
    const endTime = timeDomain[1];

    return [startTime, endTime];
}
